import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { getHeaderTheme } from "../config/headerTheme";

const theme = getHeaderTheme();

const formatDate = (isoDate) => {
    if (!isoDate) return "";
    const date = new Date(isoDate);
    return date.toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const PromocionesViewer = ({ promociones = [], onMarkAsRead }) => {
    const bottomRef = useRef(null);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [promociones]);

    return (
        <Container>
            {promociones.length === 0 ? (
                <EmptyState>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M8 10h8M8 14h5m-9 4h14a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                    </svg>
                    <p>Aún no hay novedades.</p>
                </EmptyState>
            ) : (
                <MessagesList>
                    {[...promociones]
                        .sort((a, b) => new Date(b.date) - new Date(a.date))
                        .map((promo) => (
                        <MessageRow key={promo.id} onClick={() => onMarkAsRead(promo.id)}>
                            <MessageContent>
                                <MessageHeader>
                                    <SenderName>{promo.sender}</SenderName>
                                    <MessageDate>{formatDate(promo.date)}</MessageDate>
                                </MessageHeader>
                                <Card>
                                    {promo.videoUrl ? (
                                        <CardVideo
                                            src={promo.videoUrl}
                                            title={promo.title}
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                        />
                                    ) : promo.imageUrl ? (
                                        <CardImage
                                            src={promo.imageUrl}
                                            alt={promo.title}
                                        />
                                    ) : null}
                                    <CardTitle>{promo.title}</CardTitle>
                                    <CardDescription>{promo.description}</CardDescription>
                                    {promo.actionUrl && (
                                        <CardLink
                                            href={promo.actionUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {promo.actionLabel || "Ver más"}
                                        </CardLink>
                                    )}
                                </Card>
                            </MessageContent>
                            {!promo.read && <UnreadDot />}
                        </MessageRow>
                    ))}
                    <div ref={bottomRef} />
                </MessagesList>
            )}
        </Container>
    );
};

export default PromocionesViewer;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    height: calc(100vh - 300px);
    min-height: 400px;
    background: #fff;
`;

const MessagesList = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const MessageRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    cursor: pointer;
`;

const CardImage = styled.img`
    width: 100%;
    max-height: 200px;
    object-fit: cover;
    border-radius: 8px;
`;

const CardVideo = styled.iframe`
    width: 100%;
    height: 200px;
    border: 0;
    border-radius: 8px;
    background: #000;
`;

const MessageContent = styled.div`
    flex: 1;
    min-width: 0;
`;

const MessageHeader = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 4px;
`;

const SenderName = styled.span`
    font-size: 13px;
    font-weight: 700;
    color: #111;
`;

const MessageDate = styled.span`
    font-size: 11px;
    color: #999;
`;

const Card = styled.div`
    background: #f9f9f9;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-width: 100%;

    @media (min-width: 1024px) {
        max-width: 480px;
    }
`;

const CardTitle = styled.h4`
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: #111;
`;

const CardDescription = styled.p`
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
    color: #444;
`;

const CardLink = styled.a`
    align-self: flex-start;
    background: ${theme.primary};
    color: #fff;
    text-decoration: none;
    padding: 8px 14px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;

    &:hover {
        filter: brightness(0.95);
    }
`;

const UnreadDot = styled.div`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${theme.accent};
    flex-shrink: 0;
    margin-top: 8px;
`;

const EmptyState = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #999;
    text-align: center;
    padding: 2rem 1rem;

    svg {
        width: 60px;
        height: 60px;
        margin-bottom: 1rem;
        opacity: 0.5;
    }

    p {
        font-size: 0.9rem;
        color: #666;
        margin: 0;
    }
`;
